import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import apiClient from '../../../utils/apiClient';
import { Button } from '../../../components/ui/button';
import { Card } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { Switch } from '../../../components/ui/switch';
import { Label } from '../../../components/ui/label';
import { 
  Select, SelectContent, SelectItem, 
  SelectTrigger, SelectValue 
} from '../../../components/ui/select';
import { Separator } from '../../../components/ui/separator';
import { toast } from 'sonner';
import { 
  Sparkles, Settings, Trash2, ShoppingBag, 
  FileText, Shield, CheckCircle, AlertCircle
} from 'lucide-react';

export default function TwinSkills() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [installations, setInstallations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchInstallations();
  }, []);

  const fetchInstallations = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/skills/installations', {
        params: { twin_id: 'default-twin' }
      });
      setInstallations(response.data);
    } catch (error) {
      console.error('Failed to fetch installations:', error);
      toast.error(t('errors.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleToggleEnabled = async (installationId, currentEnabled) => {
    try {
      await apiClient.put(`/skills/installations/${installationId}`, {
        enabled: !currentEnabled
      });
      
      toast.success(t('success.updated'));
      fetchInstallations();
    } catch (error) {
      console.error('Failed to toggle skill:', error);
      toast.error(t('errors.saveFailed'));
    }
  };

  const handleUpdateApprovalMode = async (installationId, approvalMode) => {
    try {
      await apiClient.put(`/skills/installations/${installationId}`, {
        approvalMode
      });
      
      toast.success(t('success.approvalModeUpdated'));
      fetchInstallations();
    } catch (error) {
      console.error('Failed to update approval mode:', error);
      toast.error(t('errors.saveFailed'));
    }
  };

  const handleUpdateDataSources = async (installationId, field, value) => {
    try {
      const installation = installations.find(i => i.installationId === installationId);
      const newDataSources = {
        ...installation.allowedDataSources,
        [field]: value
      };

      await apiClient.put(`/skills/installations/${installationId}`, {
        allowedDataSources: newDataSources
      });
      
      toast.success(t('success.dataSourcesUpdated'));
      fetchInstallations();
    } catch (error) {
      console.error('Failed to update data sources:', error);
      toast.error(t('errors.saveFailed'));
    }
  };

  const handleUninstall = async (installationId, skillName) => {
    if (!window.confirm(t('twinSkills.confirmUninstall', { skillName }))) {
      return;
    }

    try {
      await apiClient.delete(`/skills/installations/${installationId}`);
      toast.success(t('success.skillUninstalled'));
      fetchInstallations();
    } catch (error) {
      console.error('Failed to uninstall skill:', error);
      toast.error(t('errors.deleteFailed'));
    }
  };

  const getApprovalModeColor = (mode) => {
    switch (mode) {
      case 'auto': return 'bg-green-100 text-green-700';
      case 'assisted': return 'bg-blue-100 text-blue-700';
      case 'draft': return 'bg-yellow-100 text-yellow-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getApprovalModeLabel = (mode) => {
    switch (mode) {
      case 'auto': return 'Auto';
      case 'assisted': return 'Assisted';
      case 'draft': return 'Draft';
      default: return mode;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Loading Twin skills...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6" data-testid="twin-skills-page">
      {/* Header */}
      <div className="space-y-2">
        <div className="flex items-center gap-3">
          <Sparkles className="w-8 h-8 text-primary" />
          <h1 className="text-3xl font-bold text-gray-900">{t('twin.twinSkills')}</h1>
        </div>
        <p className="text-gray-600">
          Manage skills installed on your Twin. Configure approval modes and data access.
        </p>
      </div>

      {/* Add Skills Button */}
      <div className="flex justify-between items-center">
        <p className="text-sm text-gray-600">
          {installations.length} skill{installations.length !== 1 ? 's' : ''} installed
        </p>
        <Button onClick={() => navigate('/personal/marketplace')} data-testid="browse-marketplace-btn">
          <ShoppingBag className="w-4 h-4 mr-2" />
          {t('dashboard.browseSkills')}
        </Button>
      </div>

      {/* Installations List */}
      {installations.length === 0 ? (
        <Card className="p-12 text-center">
          <Sparkles className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">{t('mySkills.noSkillsInstalled')}</h3>
          <p className="text-gray-600 mb-6">
            Install skills from the marketplace to enhance your Twin's capabilities
          </p>
          <Button onClick={() => navigate('/personal/marketplace')}>
            {t('dashboard.browseSkills')}
          </Button>
        </Card>
      ) : (
        <div className="space-y-4">
          {installations.map((installation) => (
            <Card 
              key={installation.id} 
              className="p-6"
              data-testid={`installation-card-${installation.installationId}`}
            >
              <div className="space-y-4">
                {/* Header */}
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900">
                        {installation.skillName}
                      </h3>
                      {installation.enabled ? (
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      ) : (
                        <AlertCircle className="w-5 h-5 text-gray-400" />
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-xs">
                        {installation.skillCategory}
                      </Badge>
                      <Badge className={getApprovalModeColor(installation.approvalMode) + " text-xs"}>
                        {getApprovalModeLabel(installation.approvalMode)}
                      </Badge>
                      <span className="text-sm text-gray-500">v{installation.installedVersion}</span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleUninstall(installation.installationId, installation.skillName)}
                      data-testid={`uninstall-${installation.installationId}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                <Separator />

                {/* Configuration */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Enable/Disable */}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Status</Label>
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <span className="text-sm">
                        {installation.enabled ? 'Enabled' : 'Disabled'}
                      </span>
                      <Switch
                        checked={installation.enabled}
                        onCheckedChange={() => handleToggleEnabled(installation.installationId, installation.enabled)}
                        data-testid={`toggle-enabled-${installation.installationId}`}
                      />
                    </div>
                  </div>

                  {/* Approval Mode */}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">{t('twin.approvalMode')}</Label>
                    <Select 
                      value={installation.approvalMode}
                      onValueChange={(val) => handleUpdateApprovalMode(installation.installationId, val)}
                    >
                      <SelectTrigger data-testid={`approval-mode-${installation.installationId}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="draft">
                          <div className="flex items-center gap-2">
                            <Shield className="w-4 h-4" />
                            <span>{t('twin.draft')}</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="assisted">
                          <div className="flex items-center gap-2">
                            <Settings className="w-4 h-4" />
                            <span>{t('twin.assisted')}</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="auto">
                          <div className="flex items-center gap-2">
                            <Sparkles className="w-4 h-4" />
                            <span>{t('twin.auto')}</span>
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Data Sources */}
                <div className="space-y-3">
                  <Label className="text-sm font-medium">{t('twin.dataSources')}</Label>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Shield className="w-4 h-4 text-gray-600" />
                          <span className="text-sm font-medium">Training Data Access</span>
                        </div>
                        <p className="text-xs text-gray-600">Access to all training data (Style & Knowledge)</p>
                      </div>
                      <Switch
                        checked={installation.allowedDataSources.vault}
                        onCheckedChange={async (val) => {
                          // Update both vault and textTraining together
                          await handleUpdateDataSources(installation.installationId, 'vault', val);
                          await handleUpdateDataSources(installation.installationId, 'textTraining', val);
                        }}
                        data-testid={`allow-training-data-${installation.installationId}`}
                      />
                    </div>
                  </div>
                </div>

                {/* Installation Info */}
                <div className="text-xs text-gray-500 pt-2 border-t">
                  Installed: {new Date(installation.createdAt).toLocaleDateString()}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
